<template>
  <div class="pi-ai-answer">
    <p v-if="status && showStatus" class="pi-ai-answer__status">
      <span class="pi-badge" :class="'pi-badge--' + status.tone">{{ status.label }}</span>
      <pi-definition-tooltip info-key="assistant.answerStatus" :label="status.label"/>
    </p>
    <p v-if="body.headline" class="pi-ai-answer__headline"><pi-assistant-text :text="body.headline"/></p>
    <p v-if="body.periodText" class="pi-ai-answer__period">{{ body.periodText }}</p>

    <div v-for="block in body.blocks" :key="block.key" class="pi-ai-answer__block">
      <h4 v-if="block.title" class="pi-ai-answer__title"><pi-assistant-text :text="block.title"/></h4>
      <div v-for="part in block.parts" :key="part.key" class="pi-ai-answer__part">
        <p v-if="part.type === 'paragraph'" class="pi-ai-answer__text"><pi-assistant-text :text="part.text"/></p>
        <ul v-else-if="part.type === 'bullets'" class="pi-ai-answer__list">
          <li v-for="item in part.items" :key="item.key">
            <span v-if="item.link" class="pi-ai-answer__company" :class="linkState(item.link.id)" :data-link-id="item.link.id"
                  @click.capture="onLink(item.link.id, $event)" @auxclick.capture="onLink(item.link.id, $event)">
              <pi-company-name :company-id="item.link.companyId" :shard-code="item.link.shardCode" :name="item.link.name"
                               :query="linkQueryOf(item.link.query)"/>
              <span v-if="isVisited(item.link.id)" class="pi-sr"> (já aberto)</span>
            </span>
            <pi-assistant-text v-else :text="item.text"/>
          </li>
        </ul>
        <div v-else-if="part.type === 'table'" class="pi-ai-answer__table-block">
          <div v-if="exportContext" class="pi-ai-answer__table-tools">
            <pi-export-menu :title="part.title" :screen="exportContext.screen" :columns="part.sheet.columns" :rows="part.sheet.rows"
                            :envelope="exportContext.envelope" :filters="exportContext.filters" :period="exportContext.period" :register="false"/>
          </div>
          <div class="pi-table-wrap">
            <table class="pi-table pi-ai-answer__table" :aria-label="part.title || 'Tabela da resposta'">
              <thead v-if="part.header">
              <tr>
                <th v-for="column in part.columns" :key="column.key" scope="col" :class="{ 'is-num': column.numeric }"><pi-assistant-text :text="column.text"/></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in part.rows" :key="row.key">
                <td v-for="cell in row.cells" :key="cell.key" :class="{ 'is-num': cell.numeric }">
                  <span v-if="cell.link" class="pi-ai-answer__company" :class="linkState(cell.link.id)" :data-link-id="cell.link.id"
                        @click.capture="onLink(cell.link.id, $event)" @auxclick.capture="onLink(cell.link.id, $event)">
                    <pi-company-name :company-id="cell.link.companyId" :shard-code="cell.link.shardCode" :name="cell.link.name"
                                     :query="linkQueryOf(cell.link.query)"/>
                    <span v-if="isVisited(cell.link.id)" class="pi-sr"> (já aberto)</span>
                  </span>
                  <pi-assistant-text v-else :text="cell.text"/>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <p v-if="part.omitted" class="pi-muted">Mais {{ part.omitted }} linha(s) não exibidas: confira na tela do PI ou exporte a tabela.</p>
        </div>
        <ul v-else-if="part.type === 'metrics'" class="pi-stats pi-ai-answer__metrics">
          <li v-for="metric in part.metrics" :key="metric.key" class="pi-stat">
            <span class="pi-stat__label">{{ metric.label }}</span>
            <span class="pi-stat__value">{{ metric.value }}</span>
            <span v-if="metric.note" class="pi-stat__sub">{{ metric.note }}</span>
          </li>
        </ul>
      </div>
      <p v-if="block.note && block.type !== 'paragraph'" class="pi-ai-answer__note"><pi-assistant-text :text="block.note"/></p>
    </div>

    <div v-for="warning in warnings" :key="warning.key" class="pi-warn pi-ai-answer__warning" role="status">
      <span>{{ warning.text }}<pi-definition-tooltip v-if="warning.number" class="pi-ai-answer__info" info-key="assistant.unverifiedNumbers"
                                                     label="Números não conferidos"/></span>
      <span v-if="warning.number" class="pi-ai-answer__hint">{{ numberHint }}</span>
    </div>

    <div v-if="body.definitions.length" class="pi-ai-answer__section">
      <h4 class="pi-ai-answer__label">Definições usadas</h4>
      <dl class="pi-ai-answer__defs">
        <div v-for="definition in body.definitions" :key="definition.key" class="pi-ai-answer__def">
          <dt>{{ definition.term }}</dt>
          <dd><pi-assistant-text :text="definition.text"/></dd>
        </div>
      </dl>
    </div>

    <div v-if="body.caveats.length" class="pi-ai-answer__section">
      <h4 class="pi-ai-answer__label">Ressalvas</h4>
      <ul class="pi-ai-answer__list pi-ai-answer__caveats">
        <li v-for="(caveat, index) in body.caveats" :key="index"><pi-assistant-text :text="caveat"/></li>
      </ul>
    </div>

    <div v-if="links.length" class="pi-ai-answer__section">
      <h4 class="pi-ai-answer__label">Conferir no PI</h4>
      <ul class="pi-ai-answer__links">
        <li v-for="link in links" :key="link.key" @click.capture="onLink(link.key, $event)" @auxclick.capture="onLink(link.key, $event)">
          <router-link class="pi-link pi-ai-answer__link" :class="linkState(link.key)" :to="linkTo(link)" :data-link-id="link.key">
            {{ link.label }}<span v-if="isVisited(link.key)" class="pi-sr"> (já aberto)</span>
          </router-link>
        </li>
      </ul>
    </div>

    <div v-if="body.followUps.length" class="pi-ai-answer__section">
      <h4 class="pi-ai-answer__label">{{ followUpTitle }}</h4>
      <div v-if="interactive" class="pi-ai-answer__follow" role="group" :aria-label="followUpTitle">
        <button v-for="(followUp, index) in body.followUps" :key="index" type="button" class="pi-btn pi-btn--ghost pi-ai-answer__follow-btn"
                :disabled="busy" @click="$emit('follow-up', followUp)">{{ followUp }}</button>
      </div>
      <ul v-else class="pi-ai-answer__list">
        <li v-for="(followUp, index) in body.followUps" :key="index">{{ followUp }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import piAssistantText from "./piAssistantText.vue";
import piCompanyName from "./piCompanyName.vue";
import piDefinitionTooltip from "./piDefinitionTooltip.vue";
import piExportMenu from "./piExportMenu.vue";
import { NUMBER_WARNING_HINT, withChatQuery } from "./piAssistantModel.js";

const insideAnchor = (event) => {
  let node = event ? event.target : null;
  const stop = event ? event.currentTarget : null;
  while (node && node !== stop) {
    if (node.tagName === "A") return true;
    node = node.parentNode;
  }
  return !!stop && stop.tagName === "A";
};

const sameTab = (event) => !!event && event.type === "click" && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey &&
  !event.altKey;

export default {
  name: "pi-assistant-answer",
  components: { piAssistantText, piCompanyName, piDefinitionTooltip, piExportMenu },
  props: {
    body: { type: Object, required: true },
    status: { type: Object, default: null },
    showStatus: { type: Boolean, default: false },
    links: { type: Array, default: () => [] },
    warnings: { type: Array, default: () => [] },
    interactive: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    clarification: { type: Boolean, default: false },
    linkQuery: { type: Object, default: null },
    visited: { type: Array, default: () => [] },
    flash: { type: String, default: "" },
    exportContext: { type: Object, default: null },
  },
  data() {
    return { numberHint: NUMBER_WARNING_HINT };
  },
  computed: {
    followUpTitle() {
      return this.clarification ? "Qual destas?" : "Para continuar";
    },
  },
  methods: {
    linkTo(link) {
      return withChatQuery(link.to, this.linkQuery);
    },
    linkQueryOf(query) {
      return Object.assign({}, query || {}, this.linkQuery || {});
    },
    isVisited(id) {
      return this.visited.indexOf(id) !== -1;
    },
    linkState(id) {
      return { "is-visited": this.isVisited(id), "is-flash": !!this.flash && this.flash === id };
    },
    onLink(id, event) {
      if (event && event.type === "auxclick" && event.button !== 1) return;
      if (!insideAnchor(event)) return;
      this.$emit("open-link", { id: id, sameTab: sameTab(event) });
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-ai-answer__status {
  margin: 0 0 6px;
}

.pi-ai-answer__headline {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
  color: #1f2933;
}

.pi-ai-answer__period {
  margin: 0 0 12px;
  font-size: 12px;
  color: #4e5560;
}

.pi-ai-answer__block {
  margin-bottom: 12px;
}

.pi-ai-answer__title {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 700;
  color: #3a424e;
}

.pi-ai-answer__text {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #3a424e;
}

.pi-ai-answer__list {
  margin: 0 0 8px;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.5;
  color: #3a424e;
}

.pi-ai-answer__table {
  margin-bottom: 6px;
  font-size: 12px;
}

.pi-ai-answer__table-block {
  margin-bottom: 6px;
}

.pi-ai-answer__table-tools {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}

.pi-ai-answer__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-ai-answer__link.is-visited,
.pi-ai-answer__company.is-visited >>> a {
  color: #6a3db8;
}

.pi-ai-answer__link.is-flash,
.pi-ai-answer__company.is-flash {
  border-radius: 3px;
  background: #fff1b8;
  box-shadow: 0 0 0 3px #fff1b8;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

@media (prefers-reduced-motion: reduce) {
  .pi-ai-answer__link.is-flash,
  .pi-ai-answer__company.is-flash {
    transition: none;
  }
}

.pi-ai-answer__metrics {
  margin-bottom: 8px;
}

.pi-ai-answer__note {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.pi-ai-answer__warning {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pi-ai-answer__hint {
  font-weight: 400;
}

.pi-ai-answer__section {
  margin-top: 12px;
}

.pi-ai-answer__label {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  color: #4e5560;
  text-transform: uppercase;
}

.pi-ai-answer__defs {
  margin: 0;
  font-size: 12px;
}

.pi-ai-answer__def {
  margin-bottom: 4px;
}

.pi-ai-answer__def dt {
  display: inline;
  font-weight: 700;
  color: #1f2933;
}

.pi-ai-answer__def dt::after {
  content: ": ";
}

.pi-ai-answer__def dd {
  display: inline;
  margin: 0;
  color: #3a424e;
}

.pi-ai-answer__caveats {
  font-size: 12px;
  color: #4e5560;
}

.pi-ai-answer__links {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pi-ai-answer__follow {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pi-ai-answer__follow-btn {
  height: auto;
  min-height: 32px;
  max-width: 100%;
  padding: 6px 12px;
  line-height: 1.35;
  text-align: left;
  white-space: normal;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piAssistantAnswer.vue